/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  bible: {
    all: ["bible"] as const,
    /** Complete version catalog (settings + app-header SHARE cache: same query). */
    versions: (userId: string | undefined) => ["bible", "versions", userId] as const,
    /** Lightweight id/abbr/name projection for the reader. */
    versionsLite: () => ["bible", "versionsLite"] as const,
    /** Ficheros descargados de versiones (settings). */
    versionFiles: () => ["bible", "versionFiles"] as const,
    /** Highlights for a specific chapter in the reader. */
    chapterHighlights: (
      version: string | undefined,
      bookId: number | string | undefined,
      chapter: number | string | undefined,
      userId: string | undefined,
    ) => ["bible", "chapterHighlights", version, bookId, chapter, userId] as const,
    /** User highlight count (profile stats). */
    highlightCount: (userId: string | undefined) => ["bible", "highlightCount", userId] as const,
    /** User note count (profile stats). */
    noteCount: (userId: string | undefined) => ["bible", "noteCount", userId] as const,
    /** Bookmarks/favorites for a specific chapter in the reader. */
    chapterBookmarks: (
      version: string | undefined,
      bookId: number | string | undefined,
      chapter: number | string | undefined,
      userId: string | undefined,
    ) => ["bible", "chapterBookmarks", version, bookId, chapter, userId] as const,
    /**
     * User highlight colors ordered by usage frequency. Feeds
     * "used first" order of highlight sheet (parity with legacy app
     * app, where `HighlightApi.getColors` did `ORDER BY count DESC`).
     */
    highlightColorUsage: (userId: string | undefined) =>
      ["bible", "highlightColorUsage", userId] as const,
    /** Counts per book for progress/analytics view. */
    analyticsByBook: (kind: "bookmarks" | "highlights" | "notes", userId: string | undefined) =>
      ["bible", "analyticsByBook", kind, userId] as const,
  },
  bookmarks: {
    all: ["bookmarks"] as const,
    mine: (userId: string | undefined) => ["bookmarks", "mine", userId] as const,
    trashed: (userId: string | undefined) => ["bookmarks", "trashed", userId] as const,
  },
  notes: {
    all: ["notes"] as const,
    mine: (userId: string | undefined) => ["notes", "mine", userId] as const,
    tags: (userId: string | undefined) => ["notes", "tags", userId] as const,
    trashed: (userId: string | undefined) => ["notes", "trashed", userId] as const,
  },
  highlights: {
    all: ["highlights"] as const,
    mine: (bookFilter: string | number | undefined, userId: string | undefined) =>
      ["highlights", "mine", bookFilter, userId] as const,
    tags: (userId: string | undefined) => ["highlights", "tags", userId] as const,
    trashed: (userId: string | undefined) => ["highlights", "trashed", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (profile). */
    catalog: () => ["themes", "catalog"] as const,
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active user theme (theme-provider). */
    userTheme: (userId: string | undefined) => ["themes", "userTheme", userId] as const,
    /** user_settings crudo (id de tema seleccionado). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  powersync: {
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active user font family (font selector in profile). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font selector catalogs. */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  gamification: {
    all: ["gamification"] as const,
    /** Game state (XP/level/reading streak/badges) derived from annotations. */
    player: (userId: string | undefined) => ["gamification", "player", userId] as const,
    /** User notes (reading signal). */
    notes: (userId: string | undefined) => ["gamification", "notes", userId] as const,
    /** Verse-level highlights (reading signal). */
    verseHighlights: (userId: string | undefined) =>
      ["gamification", "verseHighlights", userId] as const,
    /** Group highlights (reading signal). */
    groupHighlights: (userId: string | undefined) =>
      ["gamification", "groupHighlights", userId] as const,
    /** User bookmarks (reading signal). */
    bookmarks: (userId: string | undefined) => ["gamification", "bookmarks", userId] as const,
    /** Completed reading plan days (reading signal). */
    planDays: (userId: string | undefined) => ["gamification", "planDays", userId] as const,
  },
  readingPlans: {
    all: ["readingPlans"] as const,
    /** Progress (completed days) across ALL user plans. */
    progress: (userId: string | undefined) => ["readingPlans", "progress", userId] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
    /**
     * List of devices with an active session.
     *
     * Prefix WITHOUT session id by design: `onRevokeSuccess` invalidates by
     * `activeSessionsRoot()` and reaches the entry regardless of the active
     * tab session (P23/CC-60 · P25). Previously it was a loose literal key
     * in `sessions-section.tsx` with no way to invalidate it from
     * fuera del componente.
     */
    activeSessionsRoot: () => ["auth", "activeSessions"] as const,
    activeSessions: (currentSessionId: string | undefined) =>
      ["auth", "activeSessions", { currentSessionId }] as const,
  },
};
